import type { FC } from "react";

import {
  PaginationEllipsis,
} from "@/ui/components/navigation/Pagination/PaginationEllipsis";
import {
  PaginationLink,
} from "@/ui/components/navigation/Pagination/PaginationLink";
import {
  PAGINATION_DEFAULTS,
  PAGINATION_LIST_CLASS,
  PAGINATION_NAV_CLASS,
  type TPaginationProps,
} from "@/ui/components/navigation/Pagination/Pagination.config";
import {
  getPaginationRange,
} from "@/ui/components/navigation/Pagination/Pagination.utils";
import { Icon } from "@/ui/components/primitives/Icon";
import { TextControl } from "@/ui/components/typography/TextControl";
import { cn } from "@/ui/lib/utils";

export const Pagination: FC<TPaginationProps> = (
  {
    page,
    totalPages,
    onPageChange,
    siblingCount = PAGINATION_DEFAULTS.siblingCount,
    className,
    "aria-label": ariaLabel = "pagination",
    ...props
  },
) => {
  const range = getPaginationRange({ page, totalPages, siblingCount });
  const isFirstPage = page <= 1;
  const isLastPage = page >= totalPages;

  return (
    <nav
      {...props}
      data-component="Pagination"
      aria-label={ariaLabel}
      className={cn(PAGINATION_NAV_CLASS, className)}
    >
      <ul className={PAGINATION_LIST_CLASS}>
        <li>
          <PaginationLink
            aria-label="Go to previous page"
            disabled={isFirstPage}
            onClick={() => onPageChange(page - 1)}
          >
            <Icon icon="chevronLeft" size="sm" aria-hidden="true" />
            <TextControl size={2} isResponsive={false} color="inherit">Previous</TextControl>
          </PaginationLink>
        </li>

        { range.map((item) => (
          <li key={typeof item === "number" ? `page-${item}` : item}>
            { typeof item === "number" ? (
              <PaginationLink
                isActive={item === page}
                aria-label={`Go to page ${item}`}
                onClick={() => onPageChange(item)}
              >
                <TextControl size={2} isResponsive={false} color="inherit">{ item }</TextControl>
              </PaginationLink>
            ) : (
              <PaginationEllipsis />
            ) }
          </li>
        )) }

        <li>
          <PaginationLink
            aria-label="Go to next page"
            disabled={isLastPage}
            onClick={() => onPageChange(page + 1)}
          >
            <TextControl size={2} isResponsive={false} color="inherit">Next</TextControl>
            <Icon icon="chevronRight" size="sm" aria-hidden="true" />
          </PaginationLink>
        </li>
      </ul>
    </nav>
  );
};

Pagination.displayName = "Pagination";
